// Live updates (ADR-0008). One socket per signed-in tab. A frame names the
// conversation that changed; the inbox refetches it through the REST API.
// Polling stays as the safety net: slow while the socket is open, at the old
// pace while it is not, and a reconnect refetches everything.
import { useEffect } from 'react'
import { api, unwrap } from './client'
import type { components } from './generated/schema'
import { refreshConversations, refreshInbox } from './inbox'
import { refreshAutoReplies } from './autoReplies'
import { setLiveState } from './liveState'

type RealtimeEvent = components['schemas']['RealtimeEvent']

const SESSION_EXPIRED = 4001 // the server closes at session expiry
const BATCH_MS = 300 // a burst of status webhooks becomes one refetch
const MAX_BACKOFF_MS = 30_000

// Mount once, inside the signed-in frame.
export function useRealtime(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    let stopped = false
    let socket: WebSocket | null = null
    let attempt = 0
    let opened = false
    let retry: number | undefined
    let flush: number | undefined
    const changed = new Set<string>()

    const schedule = () => {
      if (stopped) return
      setLiveState(attempt === 0 ? 'connecting' : 'offline')
      const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt) * (0.5 + Math.random() / 2)
      attempt++
      retry = window.setTimeout(() => void connect(), delay)
    }

    const connect = async () => {
      let ticket: string
      try {
        ticket = unwrap(await api.POST('/realtime/ticket')).ticket
      } catch {
        // A 401 signs out through the client middleware and unmounts this;
        // anything else is retried.
        schedule()
        return
      }
      if (stopped) return
      const proto = location.protocol === 'https:' ? 'wss' : 'ws'
      const ws = new WebSocket(`${proto}://${location.host}/v1/realtime/socket?ticket=${encodeURIComponent(ticket)}`)
      socket = ws
      ws.onopen = () => {
        attempt = 0
        setLiveState('live')
        if (opened) {
          // whatever changed while the socket was down
          refreshInbox()
          refreshAutoReplies()
        }
        opened = true
      }
      ws.onmessage = (m) => {
        let e: RealtimeEvent
        try {
          e = JSON.parse(String(m.data)) as RealtimeEvent
        } catch {
          return
        }
        if (e.type === 'settings') {
          if (e.area === 'autoReplies') refreshAutoReplies()
          return
        }
        if (e.type !== 'conversation' || !e.conversationId) return
        changed.add(e.conversationId)
        flush ??= window.setTimeout(() => {
          flush = undefined
          refreshConversations([...changed])
          changed.clear()
        }, BATCH_MS)
      }
      ws.onclose = (e) => {
        socket = null
        if (stopped) return
        if (e.code === SESSION_EXPIRED) attempt = 0 // a new ticket, or sign-in
        else attempt = Math.max(attempt, 1)
        schedule()
      }
    }

    setLiveState('connecting')
    void connect()
    return () => {
      stopped = true
      window.clearTimeout(retry)
      window.clearTimeout(flush)
      socket?.close(1000)
      setLiveState('connecting')
    }
  }, [enabled])
}
